import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  Divider,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import {
  MdDownload,
  MdWarning,
  MdCheckCircle,
  MdError,
  MdInfo
} from 'react-icons/md';
import documentService from '../services/documentService';

const CertidaoEstadualModal = ({ isOpen, onClose, certidaoData, precatorioId, state }) => {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const toast = useToast();

  if (!certidaoData) return null;

  // Determine if certificate is compromised
  const isCompromised = certidaoData.possuiDebito === true;
  const statusColor = isCompromised ? 'red' : 'green';
  const statusLabel = isCompromised ? 'CERTIDÃO COMPROMISSADA' : 'CERTIDÃO LIMPA';
  const StatusIcon = isCompromised ? MdError : MdCheckCircle;

  // Handle PDF download
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await documentService.downloadDocument(
        state,
        precatorioId,
        'certidao_estadual',
        `certidao_estadual_${certidaoData.uf}_${certidaoData.documentoConsultado}.pdf`
      );
      toast({
        title: 'Download iniciado',
        description: 'A certidão está sendo baixada.',
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      toast({
        title: 'Erro ao baixar certidão',
        description: error.message || 'Não foi possível baixar a certidão PDF',
        status: 'error',
        duration: 5000
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalContent maxW="90vw">
        <ModalHeader>
          Certidão Negativa de Débitos Estadual - {certidaoData.uf}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">

            {/* Status Header */}
            <Box
              p={6}
              borderRadius="lg"
              borderWidth="2px"
              borderColor={`${statusColor}.400`}
              bg={`${statusColor}.50`}
            >
              <VStack spacing={3}>
                <HStack spacing={3}>
                  <Icon as={StatusIcon} boxSize={8} color={`${statusColor}.600`} />
                  <Text fontSize="2xl" fontWeight="bold" color={`${statusColor}.700`}>
                    {statusLabel}
                  </Text>
                </HStack>
                <Text fontSize="lg" color="gray.700" textAlign="center">
                  {certidaoData.status || 'Status não informado'}
                </Text>
              </VStack>
            </Box>

            {/* Alert for compromised certificate */}
            {isCompromised && (
              <Alert status="error">
                <AlertIcon />
                <VStack align="start" spacing={1} flex={1}>
                  <Text fontWeight="bold">Atenção: Cliente possui pendências fiscais</Text>
                  <Text fontSize="sm">
                    O cliente possui débitos fiscais estaduais. Verifique os detalhes abaixo antes de prosseguir.
                  </Text>
                </VStack>
              </Alert>
            )}

            {/* Certificate Details */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={3} color="gray.700">
                Dados da Certidão
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Stat
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="white"
                  boxShadow="sm"
                >
                  <StatLabel>Entidade / Titular</StatLabel>
                  <StatNumber fontSize="lg" color="blue.600">
                    {certidaoData.nomeEntidade || 'Não informado'}
                  </StatNumber>
                </Stat>

                <Stat
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="white"
                  boxShadow="sm"
                >
                  <StatLabel>Documento Consultado</StatLabel>
                  <StatNumber fontSize="lg" color="purple.600">
                    {certidaoData.documentoConsultado || 'Não informado'}
                  </StatNumber>
                </Stat>

                <Stat
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="white"
                  boxShadow="sm"
                >
                  <StatLabel>Estado (UF)</StatLabel>
                  <StatNumber fontSize="lg" color="teal.600">
                    {certidaoData.uf || 'Não informado'}
                  </StatNumber>
                  {certidaoData.inscricaoEstadual && (
                    <StatHelpText>IE: {certidaoData.inscricaoEstadual}</StatHelpText>
                  )}
                </Stat>

                <Stat
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="white"
                  boxShadow="sm"
                >
                  <StatLabel>Número da Certidão</StatLabel>
                  <StatNumber fontSize="md" color="gray.700">
                    {certidaoData.numeroCertidao || 'Não informado'}
                  </StatNumber>
                </Stat>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Validity Information */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={3} color="gray.700">
                Validade
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box p={4} borderWidth="1px" borderRadius="md" bg="white">
                  <HStack spacing={2} mb={2}>
                    <Icon as={MdInfo} color="blue.500" />
                    <Text fontWeight="semibold" color="gray.700">Data de Emissão</Text>
                  </HStack>
                  <Text fontSize="lg" color="blue.600">
                    {certidaoData.dataEmissao || 'Não informada'}
                  </Text>
                </Box>

                <Box p={4} borderWidth="1px" borderRadius="md" bg="white">
                  <HStack spacing={2} mb={2}>
                    <Icon as={MdWarning} color="orange.500" />
                    <Text fontWeight="semibold" color="gray.700">Data de Validade</Text>
                  </HStack>
                  <Text fontSize="lg" color="orange.600">
                    {certidaoData.dataValidade || 'Não informada'}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Validation Code */}
            {certidaoData.codigoValidacao && (
              <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                <Text fontWeight="semibold" color="gray.700" mb={2}>
                  Código de Validação
                </Text>
                <Text fontSize="md" fontFamily="mono" color="gray.600">
                  {certidaoData.codigoValidacao}
                </Text>
              </Box>
            )}

            <Divider />

            {/* Debts Section (if compromised) */}
            {isCompromised && certidaoData.debitos && certidaoData.debitos.length > 0 && (
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={3} color="red.700">
                  Débitos Encontrados ({certidaoData.debitos.length})
                </Text>
                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="red.50"
                  borderColor="red.200"
                  maxH="300px"
                  overflowY="auto"
                >
                  <List spacing={2}>
                    {certidaoData.debitos.map((debito, index) => (
                      <ListItem key={index}>
                        <HStack align="start">
                          <ListIcon as={MdError} color="red.500" mt={1} />
                          <Text fontSize="sm" color="gray.700">{debito}</Text>
                        </HStack>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}

            {/* Clean certificate message */}
            {!isCompromised && (
              <Alert status="success">
                <AlertIcon />
                <VStack align="start" spacing={1} flex={1}>
                  <Text fontWeight="bold">Certidão Limpa</Text>
                  <Text fontSize="sm">
                    O cliente não possui pendências fiscais estaduais no estado de {certidaoData.uf}.
                  </Text>
                </VStack>
              </Alert>
            )}

            {/* Additional Observations */}
            {certidaoData.observacao && (
              <Box p={4} borderWidth="1px" borderRadius="md" bg="blue.50" borderColor="blue.200">
                <Text fontWeight="semibold" color="blue.700" mb={2}>
                  Observações
                </Text>
                <Text fontSize="sm" color="gray.700">
                  {certidaoData.observacao}
                </Text>
              </Box>
            )}

            {/* Effect of Negative */}
            {certidaoData.efeitoNegativa !== undefined && certidaoData.efeitoNegativa !== null && (
              <Box p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                <Text fontWeight="semibold" color="gray.700" mb={2}>
                  Efeito de Negativa
                </Text>
                <Badge colorScheme={certidaoData.efeitoNegativa ? 'green' : 'gray'}>
                  {certidaoData.efeitoNegativa ? 'SIM' : 'NÃO'}
                </Badge>
              </Box>
            )}

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            leftIcon={downloadingPdf ? <Spinner size="sm" /> : <MdDownload />}
            colorScheme="blue"
            mr={3}
            onClick={handleDownloadPdf}
            isDisabled={downloadingPdf}
          >
            {downloadingPdf ? 'Baixando...' : 'Baixar Certidão PDF'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CertidaoEstadualModal;
